import React, { useState, useEffect } from "react";
import { requestAddStudent,
  requestGetStudents,
  requestDeleteStudent,
  requestUpdateStudent, auth } from "../utils/resource";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faPlus,
  faXmark,
  faHouse,
  faArrowLeft,
  faArrowRight,
  faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import InputMask from "react-input-mask";
import "../styles/Students.css";

const Students = () => {
  const navigate = useNavigate();

  const [newStudent, setNewStudent] = useState({ name: "", phone: "" });
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [sortCriteria, setSortCriteria] = useState("name");

  const itemsPerPage = 5;

  const sortedStudents = (students) => {
    return students.sort((a, b) => {
      if (sortCriteria === "name") {
        return a.name.localeCompare(b.name);
      } else {
        return a.phone.localeCompare(b.phone);
      }
    });
  };

  

  useEffect(() => {
    const fetchStudents = async () => {
      const result = await requestGetStudents();
      if (result.status === "success") {
        setStudents(result.data);
        setFilteredStudents(sortedStudents(result.data));
        setTotalPages(Math.ceil(result.data.length / itemsPerPage));
      }
    };
    fetchStudents();
  }, []);

  useEffect(() => {
    const results = sortedStudents(
      students.filter((student) =>
        student.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setFilteredStudents(results);
    setTotalPages(Math.ceil(results.length / itemsPerPage));
    setCurrentPage(1);
  }, [searchTerm, students]);

  const handleUpdateStudent = async (e) => {
    e.preventDefault();

    if (!newStudent.name.trim() || !newStudent.phone.trim()) {
      alert("Por favor, preencha os campos nome e telefone.");
      return;
    }

    if (selectedStudent) {
      const result = await requestUpdateStudent(
        selectedStudent.id,
        newStudent.name,
        newStudent.phone
      );
      if (result.status === "success") {
        const updatedStudents = students.map((student) =>
          student.id === selectedStudent.id
            ? { ...student, name: newStudent.name, phone: newStudent.phone }
            : student
        );
        setStudents(updatedStudents);
        setFilteredStudents(updatedStudents);
        setSelectedStudent(null);
        setNewStudent({ name: "", phone: "" });
        setIsAdding(false);
      }
    } else {
      const result = await requestAddStudent(newStudent.name, newStudent.phone);
      if (result.status === "success") {
        const updatedStudents = [...students, { id: result.id, ...newStudent }];
        setStudents(updatedStudents);
        setFilteredStudents(updatedStudents);
        setNewStudent({ name: "", phone: "" });
        setIsAdding(false);
      }
    }
  };

  const handleCancel = () => {
    navigate("/dashboard");
  };

  const handleDeleteStudent = async (studentId, studentName) => {
    const confirmDelete = window.confirm(
      `Tem certeza que deseja excluir o(a) aluno(a) ${studentName}?`
    );

    if (confirmDelete) {
      try {
        const response = await requestDeleteStudent(studentId);
        if (response.status === "success") {
          setStudents((prevStudents) =>
            prevStudents.filter((student) => student.id !== studentId)
          );
        } else {
          alert("Erro ao excluir aluno. Tente novamente.");
        }
      } catch (error) {
        console.error("Erro ao deletar aluno:", error);
        alert("Erro ao excluir aluno. Tente novamente.");
      }
    }
  };

  const handleEditStudent = (student) => {
    setNewStudent({ name: student.name, phone: student.phone });
    setSelectedStudent(student);
    setIsAdding(true);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleStudentClick = (student) => {
    setSelectedStudent(student);
    setIsAdding(false);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const paginatedStudents = filteredStudents.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const renderPageNumbers = () => {
    const pages = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={i === currentPage ? "active" : ""}
          >
            {i}
          </button>
        );
      }
      return pages;
    }

    pages.push(
      <button
        key={1}
        onClick={() => handlePageChange(1)}
        className={1 === currentPage ? "active" : ""}
      >
        1
      </button>
    );

    if (currentPage > 4) {
      pages.push(
        <span key="ellipsis-start" className="pagination-ellipsis">
          ...
        </span>
      );
    }

    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage <= 3) {
      end = 4;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 3;
    }

    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={i === currentPage ? "active" : ""}
        >
          {i}
        </button>
      );
    }

    if (currentPage < totalPages - 3) {
      pages.push(
        <span key="ellipsis-end" className="pagination-ellipsis">
          ...
        </span>
      );
    }

    pages.push(
      <button
        key={totalPages}
        onClick={() => handlePageChange(totalPages)}
        className={totalPages === currentPage ? "active" : ""}
      >
        {totalPages}
      </button>
    );

    return pages;
  };

  return (
    <div className="students-container">
      <main className="students-main">
        <div className="button-bar">
          {isAdding ? (
            <>
              <FontAwesomeIcon
                icon={faSave}
                className="icon save-icon"
                onClick={handleUpdateStudent}
                title="Salvar"
              />
              <FontAwesomeIcon
                icon={faXmark}
                className="icon cancel-icon"
                onClick={() => {
                  setIsAdding(false);
                  setNewStudent({ name: "", phone: "" });
                  setSelectedStudent(null);
                }}
                title="Cancelar"
              />
            </>
          ) : (
            <>
              <FontAwesomeIcon
                icon={faPlus}
                className="icon add-icon"
                onClick={() => setIsAdding(true)}
                title="Adicionar Estudante"
              />
              <FontAwesomeIcon
                icon={faHouse}
                className="icon cancel-icon"
                onClick={handleCancel}
                title="Cancelar"
              />
            </>
          )}
        </div>

        {isAdding && (
          <div className="students-form">
            <div className="form-group">
              <label htmlFor="studentName">Nome</label>
              <input
                type="text"
                id="studentName"
                value={newStudent.name}
                onChange={(e) =>
                  setNewStudent({ ...newStudent, name: e.target.value })
                }
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="studentPhone">Celular</label>
              <InputMask
                mask="(99) 99999-9999"
                id="studentPhone"
                value={newStudent.phone}
                onChange={(e) =>
                  setNewStudent({ ...newStudent, phone: e.target.value })
                }
                required
              />
            </div>
          </div>
        )}

        <div className="students-search">
          <input
            type="text"
            placeholder="Buscar por nome ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="students-list">
          <p className="students-list-title">
            Lista de Alunos ({filteredStudents.length})
          </p>
          {paginatedStudents.length > 0 ? (
            <ul>
              {paginatedStudents.map((student) => (
                <li key={student.id}>
                  <p onClick={() => handleStudentClick(student)}>
                    {student.name}
                  </p>
                  <FontAwesomeIcon
                    icon={faPenToSquare}
                    className="icon edit-icon"
                    onClick={() => handleEditStudent(student)}
                    title="Editar"
                  />
                  <FontAwesomeIcon
                    icon={faXmark}
                    className="icon delete-icon"
                    onClick={() =>
                      handleDeleteStudent(student.id, student.name)
                    }
                    title="Excluir"
                  />
                </li>
              ))}
            </ul>
          ) : (
            <p>Nenhum aluno encontrado.</p>
          )}

          <div className="pagination">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            {renderPageNumbers()}
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
        </div>

        {selectedStudent && !isAdding && (
          <div className="student-details">
            <p>
              <strong>Nome:</strong> {selectedStudent.name}
            </p>
            <p>
              <strong>Celular:</strong> {selectedStudent.phone}
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Students;
